import {
	Dialog,
	Form,
	FormItem,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IValidationConfiguration
} from "client/Validation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useIsTouchDevice,
	useUnmountedCancellation
} from "client/Hooks";
import { isNotEmpty } from "client/Validation/rules";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	OAuth2EnableMicrosoftAuthenticatorMobileSignInResult,
	SingleSignOnPasswordModel,
	enableMicrosoftAuthenticatorMobileSignIn
} from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type EnableMicrosoftAuthenticatorMobileSignInDialogProps = {
	onSuccess: () => void;
	onClose: () => void;
};

export const EnableMicrosoftAuthenticatorMobileSignInDialog = (
	props: EnableMicrosoftAuthenticatorMobileSignInDialogProps
): ReactElement => {
	const {
		onSuccess,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [formData, setFormData] = useState<SingleSignOnPasswordModel>({ password: "" });
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isTouch: boolean = useIsTouchDevice();

	const formHandler: IFormHandler<SingleSignOnPasswordModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind === InputValidationResultKind.Invalid) {
					setIsBusy(false);
					return;
				}

				const result: OAuth2EnableMicrosoftAuthenticatorMobileSignInResult = await enableMicrosoftAuthenticatorMobileSignIn(
					formData,
					globalCancellation
				);

				setIsBusy(false);

				if (result.wasSuccessful) {
					onSuccess();
				} else {
					alertManager.showError(
						<>
							<div>{localize("_CouldNotEnableMicrosoftAuthenticatorSignIn")}</div>
							<div>{result.reason}</div>
						</>
					);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onSuccess
		]
	);

	return (
		<Dialog
			trackingId="enable-microsoft-authenticator-mobile-sign-in-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_EnableMicrosoftAuthenticatorSignIn")}
				onClose={onClose}
			/>
			<Dialog.Body>
				<Form onSubmit={saveHandler}>
					<FormItem label={localize("_Password")} isRequired>
						<TextInput
							{...formHandler.for("password").attachToTextInput()}
							type="password"
							placeholder={localize("_EnterPassword")}
							autoFocus={!isTouch ? "focus" : undefined}
						/>
						<ValidationError {...formHandler.for("password").attachToValidationError()} />
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						trackingId="enable"
						kind="primary"
						text={localize("_Enable")}
						onClick={saveHandler}
						isDisabled={formData.password === ""}
					/>
					<Toolbar.Button
						trackingId="cancel"
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EnableMicrosoftAuthenticatorMobileSignInDialog.displayName = "EnableMicrosoftAuthenticatorMobileSignInDialog";

const validationConfiguration: IValidationConfiguration<SingleSignOnPasswordModel> =
	createValidationConfiguration<SingleSignOnPasswordModel>();

validationConfiguration
	.forScalar("password")
	.add(
		isNotEmpty()
	);

export function useEnableMicrosoftAuthenticatorMobileSignInDialog(): [
	element: ReactElement | null,
	open: () => Promise<TryResult<void>>
] {
	return useDataInputDialog<void, void>(
		(_, onClose, onSave) => (
			<EnableMicrosoftAuthenticatorMobileSignInDialog
				onClose={onClose}
				onSuccess={onSave}
			/>
		)
	);
}
